import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    BookOpen,
    CalendarDays,
    FileText,
    UserCircle,
    LogOut,
    Menu,
    ChevronRight,
    Mail,
} from "lucide-react";
import { useAuth, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import {
    StagiaireActionsProvider,
    useStagiaireActions,
} from "@/components/stagiaire/StagiaireActionsContext";

const NAV = [
    { to: "/stagiaire", label: "Dashboard", icon: LayoutDashboard, end: true, sectionKey: null },
    { to: "/stagiaire/cursus", label: "Mes cursus", icon: BookOpen, sectionKey: null },
    { to: "/stagiaire/sessions", label: "Mes sessions", icon: CalendarDays, sectionKey: "sessions" },
    { to: "/stagiaire/documents", label: "Mes documents", icon: FileText, sectionKey: "documents" },
    { to: "/stagiaire/profil", label: "Mon profil", icon: UserCircle, sectionKey: "profil" },
    { to: "/stagiaire/contact", label: "Contact", icon: Mail, sectionKey: null },
];

function crumbFor(pathname) {
    if (pathname === "/stagiaire") return "Dashboard";
    if (pathname.startsWith("/stagiaire/cursus")) return "Cursus";
    if (pathname.startsWith("/stagiaire/sessions")) return "Sessions";
    if (pathname.startsWith("/stagiaire/documents")) return "Documents";
    if (pathname.startsWith("/stagiaire/profil")) return "Mon profil";
    if (pathname.startsWith("/stagiaire/contact")) return "Contact";
    return "Stagiaire";
}

export default function StagiaireLayout() {
    return (
        <StagiaireActionsProvider>
            <StagiaireLayoutInner />
        </StagiaireActionsProvider>
    );
}

function StagiaireLayoutInner() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const { data: actions } = useStagiaireActions();
    const counters = actions?.counters || {};

    const handleLogout = async () => {
        await logout();
        toast.success("Déconnecté");
        navigate("/login", { replace: true });
    };

    const Sidebar = (
        <aside className="flex h-screen w-72 flex-col bg-brand-ink text-white">
            <div className="flex flex-shrink-0 items-center gap-3 px-6 py-7 border-b border-white/10">
                <img src={LOGO_URL} alt="Cursus Physio" className="h-8 w-auto brightness-0 invert" />
            </div>
            <div className="flex-shrink-0 px-6 py-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40">
                Espace stagiaire
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-4 pb-4">
                {NAV.map((item) => {
                    const Icon = item.icon;
                    const count = item.sectionKey ? counters[item.sectionKey] || 0 : 0;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setOpen(false)}
                            data-testid={`stagiaire-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-brand-orange text-white shadow-[0_4px_16px_rgba(232,135,60,0.3)]"
                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {count > 0 && (
                                <span
                                    data-testid={`stagiaire-nav-badge-${item.sectionKey}`}
                                    className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white ring-2 ring-brand-ink"
                                    title={`${count} action${count > 1 ? "s" : ""} à faire`}
                                >
                                    {count}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>
            <div className="flex-shrink-0 border-t border-white/10 p-4">
                <div className="mb-3 rounded-xl bg-white/[0.03] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Connecté</div>
                    <div className="mt-1 truncate text-sm font-medium">{user?.first_name} {user?.last_name}</div>
                    <div className="mt-0.5 truncate text-xs text-white/50">{user?.email}</div>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="stagiaire-logout"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                >
                    <LogOut className="h-4 w-4" /> Déconnexion
                </button>
            </div>
        </aside>
    );

    return (
        <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="stagiaire-layout">
            <div className="sticky top-0 hidden h-screen lg:flex">{Sidebar}</div>
            {open && (
                <div className="fixed inset-0 z-40 flex lg:hidden">
                    <div className="absolute inset-0 bg-brand-ink/50" onClick={() => setOpen(false)} />
                    <div className="relative h-full">{Sidebar}</div>
                </div>
            )}
            <div className="flex min-h-screen flex-1 flex-col">
                <header className="flex items-center gap-4 border-b border-brand-line bg-white px-6 py-4">
                    <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Menu">
                        <Menu className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-2 text-sm text-brand-smoke">
                        <Link to="/stagiaire" className="hover:text-brand-ink">Espace stagiaire</Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="font-medium text-brand-ink">{crumbFor(location.pathname)}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden text-right md:block">
                            <div className="text-sm font-medium text-brand-ink">{user?.first_name} {user?.last_name}</div>
                            <div className="text-xs text-brand-smoke">Stagiaire</div>
                        </div>
                        <div className="relative">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white font-display text-sm">
                                {(user?.first_name || "?")[0]?.toUpperCase()}
                            </div>
                            {(actions?.total || 0) > 0 && (
                                <span
                                    data-testid="stagiaire-header-badge"
                                    className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 py-0 text-[10px] font-bold text-white ring-2 ring-white"
                                    title={`${actions.total} action${actions.total > 1 ? "s" : ""} à faire`}
                                >
                                    {actions.total}
                                </span>
                            )}
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
